import { TEST_TYPES } from '@hawkindynamics/core-client/modules/test_types';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { IForceTimeData, IGraph, ITest, ITimingEvent } from '../data/types';
import { testPhases } from './testPhases';

interface ITestConfig {
  id: string;
  start: (t: ITest) => number;
  movementStart: (t: ITest) => number;
  movementEnd: (t: ITest) => number;
  end: (t: ITest, h?: number) => number;
}

interface ITestConfigMap {
  [key: string]: ITestConfig;
}

/**
 * Creates a test configuration object with the specified timing functions
 *
 * @param id - The test type identifier
 * @param start - Function to determine the start time of the test
 * @param movementStart - Function to determine when the movement starts
 * @param movementEnd - Function to determine when the movement ends
 * @param end - Function to determine the end time of the test
 * @returns A complete test configuration object
 */
const createTestConfig = (
  id: string,
  start: (t: ITest) => number,
  movementStart: (t: ITest) => number,
  movementEnd: (t: ITest) => number,
  end: (t: ITest, h?: number) => number
): ITestConfig => ({
  id,
  start,
  movementStart,
  movementEnd,
  end,
});

/**
 * Maps test types to their corresponding test configuration objects
 *
 * @type {ITestConfigMap}
 */
const truncateTest: ITestConfigMap = {
  [TEST_TYPES.CMJ.id]: createTestConfig(
    TEST_TYPES.CMJ.id,
    (test: ITest): number => test.t3,
    (test: ITest): number => test.t3,
    (test: ITest): number => test.t6,
    (test: ITest, hertz: number = 1000): number =>
      (test.t7 !== 'N/A' ? test.t7 : test.t6) + Math.floor(hertz)
  ),
  [TEST_TYPES.SQUAT.id]: createTestConfig(
    TEST_TYPES.SQUAT.id,
    (test: ITest): number => test.t2,
    (test: ITest): number => test.t3,
    (test: ITest): number => test.t4,
    (test: ITest, hertz: number = 1000): number =>
      (test.t5 !== 'N/A' ? test.t5 : test.t4) + Math.floor(hertz)
  ),
  [TEST_TYPES.ISO.id]: createTestConfig(
    TEST_TYPES.ISO.id,
    (test: ITest): number => test.t3,
    (test: ITest): number => test.t3,
    (test: ITest): number => test.t6,
    (test: ITest, hertz: number = 1000): number => test.t6 + Math.floor(hertz)
  ),
  [TEST_TYPES.REBOUND.id]: createTestConfig(
    TEST_TYPES.REBOUND.id,
    (test: ITest): number => test.t3,
    (test: ITest): number => test.t3,
    (test: ITest): number => test.t6,
    (test: ITest, hertz: number = 1000): number =>
      (test.t10 !== 'N/A' ? test.t10 : test.t9) + Math.floor(hertz)
  ),
  [TEST_TYPES.DROP.id]: createTestConfig(
    TEST_TYPES.DROP.id,
    (test: ITest): number => test.t1,
    (test: ITest): number => test.t1,
    (test: ITest): number => test.t3,
    (test: ITest, hertz: number = 1000): number =>
      (test.t4 !== 'N/A' ? test.t4 : test.t3) + Math.floor(hertz)
  ),
  [TEST_TYPES.DROP_LANDING.id]: createTestConfig(
    TEST_TYPES.DROP_LANDING.id,
    (test: ITest): number => test.t1,
    (test: ITest): number => test.t1,
    (test: ITest): number => test.t4,
    (test: ITest, hertz: number = 1000): number => test.t4
  ),
};

/**
 * Calculates the shift value based on the sampling rate
 *
 * @param hertz - The sampling rate in Hz (default: 1000)
 * @returns The number of samples to shift
 */
export const shiftValue = (hertz: number = 1000): number =>
  Math.floor(0.3 * hertz);

/**
 * @description extracts the "t" properties from a test an shifts them accordingly based on how much of the
 * raw force was trimmed from the start of the test
 * @param test {ITest}
 * @returns { [index: string]: number }
 */
export const tVals = (test: ITest): { [index: string]: number } => {
  if (!isObject(test)) return {};

  return Object.entries(test)
    .filter(([key]: [string, unknown]): boolean => /^t[1-9]/.test(key))
    .sort(([a]: [string, unknown], [b]: [string, unknown]): number => {
      const numA = parseInt(a.match(/\d+/)?.[0] || '0', 10);
      const numB = parseInt(b.match(/\d+/)?.[0] || '0', 10);
      return numA - numB;
    })
    .reduce(
      (
        acc: { [index: string]: number },
        [key, value]: [string, number]
      ): { [index: string]: number } => ({
        ...acc,
        [key]: value,
      }),
      {}
    );
};

/**
 * @description "trims" the graph data to exclude uninteresting data recorded at the beginning or after the end of the
 * action portion of the test
 * @param test {ITest}
 * @param data {number[][]}
 */
export const shiftGraphData = (
  config: ITestConfig,
  test: ITest,
  data: number[] = [],
  hertz: number = 1000
): [boolean, number[]] => {
  if (!isArray(data)) return [false, []];
  if (config && data.length > 0) {
    const start = config.start(test) - shiftValue(hertz);
    const end = config.end(test, hertz);
    const res = data.slice(start, isNaN(end) ? data.length - 1 : end);
    return [true, res];
  }
  return [false, data];
};

/**
 * Extracts the movement portion of the test data
 *
 * @param test - The test object containing timing information
 * @param values - The array of values to extract movement data from
 * @returns A tuple containing the start index and the movement data array
 */
export const movementData = (
  test: ITest,
  values: number[]
): [number, number[]] => {
  const config = truncateTest[test.testType?.id];
  if (!config) return [null, []];

  const start = config.movementStart(test);
  const end = config.movementEnd(test);
  return [start, values.slice(start, end)];
};

type GraphDataMapper = (data: number[] | undefined) => number[][];

/**
 * Creates a base graph data object for a test
 *
 * @param test - The test object containing test type and timing information
 * @param data - The graph data object containing force and timing data
 * @param mapper - The mapper function to apply to the data
 */
const createBaseGraphData = (
  test: ITest,
  data: IGraph,
  mapper: GraphDataMapper
): IForceTimeData => {
  return {
    testType: test.testType,
    zForce: mapper(data.zForce),
    zForceLeft: mapper(data.zForceLeft),
    zForceRight: mapper(data.zForceRight),
    xForceLeft: mapper(data.xForceLeft),
    xForceRight: mapper(data.xForceRight),
    yForceLeft: mapper(data.yForceLeft),
    yForceRight: mapper(data.yForceRight),
    velocity: mapper(data.velocity),
    displacement: mapper(data.displacement),
    power: mapper(data.power),
    rsi: isArray(data.rsi) ? data.rsi.map(rsiMapper) : [],
    timingEvents: createTimingEvents(test),
    length: data.zForce.length,
    hertz: data.hertz || 1000,
  };
};

/**
 * Creates a graph data object for a test
 *
 * @param test - The test object containing test type and timing information
 * @param data - The graph data object containing force and timing data
 * @returns A force time data object with mapped and shifted data
 */
export const makeGraphData = (test: ITest, data: IGraph): IForceTimeData => {
  const hertz = data.hertz || 1000;
  const config = truncateTest[test.testType?.id];

  if (isEmpty(config)) {
    return createBaseGraphData(test, data, timeMapperFactory());
  }

  const shiftMapper = shiftMapperFactory(
    config,
    test,
    config.start(test),
    hertz
  );

  return {
    ...createBaseGraphData(test, data, shiftMapper),
    trimPoint: config.start(test),
  };
};

/**
 * Creates a mapper function for time-based data
 *
 * @param hertz - The sampling rate in Hz (default: 1000)
 * @returns A function that maps data points to time-based coordinates
 */
const timeMapperFactory = (): GraphDataMapper => {
  return (data: number[] | undefined): number[][] => {
    if (!data) return [];
    return data.map((item: number, index: number): number[] => {
      return [index + 1, item];
    });
  };
};

/**
 * Creates a mapper function for shifted data based on test configuration
 *
 * @param config - The test configuration object
 * @param test - The test object containing timing information
 * @param startOffset - The starting offset for the data
 * @param hertz - The sampling rate in Hz
 * @returns A function that maps data points to shifted coordinates
 */
const shiftMapperFactory = (
  config: ITestConfig,
  test: ITest,
  startOffset: number,
  hertz: number
): GraphDataMapper => {
  // Debug log removed to avoid unintended console output in production.
  return (data: number[] | undefined): number[][] => {
    if (!data) return [];
    const [didShift, mData] = shiftGraphData(config, test, data, hertz);
    return mData.map((item: number, index: number): number[] => {
      const mIndex = didShift
        ? index + startOffset + 1 - shiftValue(hertz)
        : index + startOffset + 1;
      return [Math.floor(mIndex), item];
    });
  };
};

/**
 * Maps RSI data points to coordinates
 *
 * @param item - The RSI value
 * @param index - The index of the data point
 * @returns An array containing the index and RSI value
 */
const rsiMapper = (item: number, index: number): number[] => {
  return [].concat([index + 1], item);
};

/**
 * Creates timing events for visualization based on test configuration
 *
 * @param test - The test object containing timing information
 * @returns An array of timing events with start, end, and color information
 */
export const createTimingEvents = (test: ITest): ITimingEvent[] => {
  const timingValues = tVals(test);
  if (Object.keys(timingValues).length === 0) return [];

  const testTypePhaseConfig = testPhases[test.testType.id];
  if (!testTypePhaseConfig) return [];

  return Object.keys(testTypePhaseConfig).map((phase: string): ITimingEvent => {
    return {
      name: phase,
      start: timingValues[testTypePhaseConfig[phase].start],
      end: timingValues[testTypePhaseConfig[phase].end],
      color: testTypePhaseConfig[phase].color.dark,
    };
  });
};
